import React, { useState, useEffect } from "react";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { ChevronRight } from "lucide-react";
import { ScalarKind } from "../types";
import ScalarInput from "./ScalarInput";
import { SimpleValueRenderer } from "../Components/FactRenderer";
import HierarchicalDropdown from "./HierarchicalDropdown";

interface HierarchicalInputProps {
  scalarType: ScalarKind | null;
  scalarUnit?: string | null;
  scalarValues?: any[] | null;
  value: any[] | null;
  onChange: (value: any[] | null) => void;
  // Forwarded to HierarchicalDropdown so the trigger button paints
  // pl-24 / pr-24 around overlaid Autofill / Source chips. The parent's
  // `[&_input]:pl-24` arbitrary-variant selectors don't reach buttons.
  hasAutofillChip?: boolean;
  hasSourceChip?: boolean;
}

const HierarchicalInput: React.FC<HierarchicalInputProps> = ({
  scalarType,
  scalarUnit,
  scalarValues,
  value,
  onChange,
  hasAutofillChip = false,
  hasSourceChip = false,
}) => {
  const [buildingValue, setBuildingValue] = useState<any[]>(
    Array.isArray(value) ? value : value ? [value] : []
  );
  const [currentInput, setCurrentInput] = useState<any>(null);

  // Sync value prop with state to ensure buildingValue is always an array
  useEffect(() => {
    setBuildingValue(Array.isArray(value) ? value : value ? [value] : []);
  }, [value]);

  const handleAddLevel = () => {
    if (
      currentInput !== null &&
      currentInput !== undefined &&
      currentInput !== ""
    ) {
      // Check if the input contains " > " to parse nested hierarchy
      const inputStr = String(currentInput);
      if (inputStr.includes(" > ")) {
        // Split by " > " and add all levels at once
        const levels = inputStr
          .split(" > ")
          .map((level) => level.trim())
          .filter((level) => level !== "");
        if (levels.length > 0) {
          const newValue = [...buildingValue, ...levels];
          setBuildingValue(newValue);
          onChange(newValue);
          setCurrentInput(null);
        }
      } else {
        // Add single level as before
        const newValue = [...buildingValue, currentInput];
        setBuildingValue(newValue);
        onChange(newValue);
        setCurrentInput(null);
      }
    }
  };

  const handleRemoveLevel = (index: number) => {
    const newValue = buildingValue.filter((_, i) => i !== index);
    setBuildingValue(newValue);
    onChange(newValue.length > 0 ? newValue : null);
  };

  // For enums with predefined values, use a dropdown
  if (scalarValues && scalarValues.length > 0) {
    return (
      <HierarchicalDropdown
        scalarType={scalarType}
        scalarUnit={scalarUnit}
        options={scalarValues}
        value={value}
        onChange={onChange}
        placeholder="Select a value..."
        hasAutofillChip={hasAutofillChip}
        hasSourceChip={hasSourceChip}
      />
    );
  }

  // For non-enum fields, use the builder
  return (
    <div className="space-y-2">
      {/* Display current hierarchy */}
      {buildingValue.length > 0 && (
        <div className="flex items-center flex-wrap gap-1 p-2 bg-gray-50 rounded-md">
          {buildingValue.map((level, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-sm">
                <SimpleValueRenderer
                  value={level}
                  scalarType={scalarType}
                  scalarUnit={scalarUnit}
                />
                <button
                  onClick={() => handleRemoveLevel(index)}
                  className="hover:bg-gray-100 rounded p-0.5"
                  type="button"
                >
                  <XIcon size={12} className="text-gray-500" />
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Input for next level */}
      <div
        className="flex gap-2"
        onKeyDown={(e) => {
          // Enter (without Shift): Add a level
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAddLevel();
          }
        }}
      >
        <div className="flex-1">
          <ScalarInput
            scalarType={scalarType}
            scalarUnit={scalarUnit}
            scalarValues={null} // Don't pass enum values to avoid confusion
            value={currentInput}
            onChange={setCurrentInput}
            isHierarchical={false} // Prevent infinite recursion
            placeholder="Add a level (use > to nest)"
          />
        </div>
        <button
          onClick={handleAddLevel}
          disabled={
            currentInput === null ||
            currentInput === undefined ||
            currentInput === ""
          }
          className="h-[38px] px-3 flex items-center justify-center bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          type="button"
        >
          <PlusIcon size={16} />
        </button>
      </div>
    </div>
  );
};

export default HierarchicalInput;
